// SDP spending-plan entry rules (per-company flag "SDP spending-plan entry").
// With the flag on, the Spending Plan is where budgets are entered: each row is one provider
// under a service code, the code's authorization is the sum of its rows, and Activate turns the
// plan into POS authorizations + worker/vendor assignments on the backend.

export const PROVIDER_TYPE_DSW = 'DSW';
export const PROVIDER_TYPE_VENDOR = 'Vendor';

export const PLACEHOLDER_PROVIDER_NAME = 'To be determined';

// Until the backend add-provider endpoint exists, a provider added from the picker is kept in this
// browser only: it fills the row (unlinked, so it still blocks Activate), is offered again in later
// searches, and restores the row's provider type on reload if the line save doesn't keep it
const LOCAL_PROVIDERS_KEY = 'sdpLocalProviders';

export function getLocalProviders() {
    try {
        const list = JSON.parse(localStorage.getItem(LOCAL_PROVIDERS_KEY) || '[]');
        return Array.isArray(list) ? list : [];
    } catch {
        return [];
    }
}

export function saveLocalProvider(provider) {
    try {
        const list = getLocalProviders().filter(p => p.name.toLowerCase() !== provider.name.toLowerCase());
        localStorage.setItem(LOCAL_PROVIDERS_KEY, JSON.stringify([...list, provider]));
    } catch { /* ignore */ }
}

export function getLocalProviderType(name) {
    const key = String(name || '').trim().toLowerCase();
    if (!key) return null;
    return getLocalProviders().find(p => p.name.toLowerCase() === key)?.providerType || null;
}

// Lines added on the SDP screen (vs. lines from the old flow that were only loaded), so they can be
// told apart after saving. Kept in this browser until the backend stores it on the line. (Not taken
// from ProviderType: saving an old line on the SDP screen sends one too.)
const SDP_ENTERED_LINES_KEY = 'sdpEnteredLineIds';

export function getSdpEnteredLineIds() {
    try {
        const list = JSON.parse(localStorage.getItem(SDP_ENTERED_LINES_KEY) || '[]');
        return new Set((Array.isArray(list) ? list : []).map(String));
    } catch {
        return new Set();
    }
}

export function markSdpEnteredLines(lineIds) {
    try {
        const ids = getSdpEnteredLineIds();
        (lineIds || []).forEach(id => { if (id !== null && id !== undefined) ids.add(String(id)); });
        localStorage.setItem(SDP_ENTERED_LINES_KEY, JSON.stringify([...ids]));
    } catch { /* ignore */ }
}

// Each SDP line's provider type (Worker/Vendor) by LineId, kept in this browser because the line
// save doesn't store ProviderType yet. Without it a reloaded vendor line would come back as a worker
// and its amount would be recalculated from base pay (0).
const LINE_PROVIDER_TYPES_KEY = 'sdpLineProviderTypes';

function getLineProviderTypes() {
    try {
        const map = JSON.parse(localStorage.getItem(LINE_PROVIDER_TYPES_KEY) || '{}');
        return map && typeof map === 'object' ? map : {};
    } catch {
        return {};
    }
}

export function rememberLineProviderTypes(lineIds, providerType) {
    if (!providerType) return;
    try {
        const map = getLineProviderTypes();
        (lineIds || []).forEach(id => { if (id !== null && id !== undefined) map[String(id)] = providerType; });
        localStorage.setItem(LINE_PROVIDER_TYPES_KEY, JSON.stringify(map));
    } catch { /* ignore */ }
}

export function getRememberedLineProviderType(lineId) {
    return getLineProviderTypes()[String(lineId)] || null;
}

export function isSdpEnteredLine(line) {
    return getSdpEnteredLineIds().has(String(line?.LineId));
}

// Company config entry (getconfigurablelist) that turns SDP entry on for a business
export const SDP_CONFIG_TABLE_ID = 'SDPSpendingPlanEntry';

export function isSdpEntryEnabled(configList) {
    const entry = (configList || []).find(item => item?.TableID === SDP_CONFIG_TABLE_ID);
    if (!entry) return false;
    const value = String(entry.Value ?? entry.Description ?? '').trim().toLowerCase();
    return value === '1' || value === 'true' || value === 'yes' || value === 'on';
}

function getServiceCodeNumber(serviceCode) {
    const m = String(serviceCode || '').match(/^\s*(\d+)/);
    return m ? parseInt(m[1], 10) : null;
}

export function getRowProviderType(row) {
    return row?.providerType === PROVIDER_TYPE_VENDOR ? PROVIDER_TYPE_VENDOR : PROVIDER_TYPE_DSW;
}

// A line saved before provider types existed has none; payroll tells them apart the same way the
// dollars do (a worker has an hourly PayRate, a vendor is paid invoice charges). Null = can't tell.
export function inferProviderTypeFromPayroll(payrollEntry) {
    if (!payrollEntry) return null;
    if (Number(payrollEntry.payRate) > 0) return PROVIDER_TYPE_DSW;
    if (Number(payrollEntry.totalChargeAmount) > 0) return PROVIDER_TYPE_VENDOR;
    return null;
}

export function isVendorRow(row) {
    return getRowProviderType(row) === PROVIDER_TYPE_VENDOR;
}

// LiveIn comes back from the API as true/false, 1/0 or strings
export function isLiveIn(value) {
    if (!value) return false;
    const s = String(value).trim().toLowerCase();
    return !(s === 'false' || s === '0' || s === 'no' || s === 'n');
}

// EVV is derived, never entered: vendors are never EVV; a DSW is EVV on Living Arrangement codes
// (310-330, location-bound in-home work) unless live-in, and not EVV on Employment & Community
// Participation codes (331-355, community work) or anything else.
export function isRowEvv(row) {
    if (isVendorRow(row)) return false;
    if (isLiveIn(row?.liveIn)) return false;
    const code = getServiceCodeNumber(row?.serviceCode);
    return code !== null && code >= 310 && code <= 330;
}

// Live-in only changes anything on EVV-by-default (310-330) DSW rows
export function canRowBeLiveIn(row) {
    if (isVendorRow(row)) return false;
    const code = getServiceCodeNumber(row?.serviceCode);
    return code !== null && code >= 310 && code <= 330;
}

// A row is linked when it points at a real provider record; placeholders and typed-in names are
// not, and block Activate until they are.
export function isRowLinked(row) {
    return Boolean(row?.workerId);
}

// DSW: pricing unit (pay + burden) x hours. Vendor: amount x count. Both are stored as
// pricingUnit x frequency so one formula totals every row.
export function getRowTotal(row) {
    return (Number(row?.pricingUnit) || 0) * (Number(row?.frequency) || 0);
}

export function sumRows(rows) {
    return (rows || []).reduce((sum, r) => sum + getRowTotal(r), 0);
}

const round2 = (n) => Math.round((Number(n) + Number.EPSILON) * 100) / 100;

// A worker's custom burden is kept in the line's PaymentModel text as "Custom 12.34%", so it saves
// with the line without a new field. Returns the percent, or null for any other payment model.
export function parseCustomBurdenPercent(paymentModel) {
    const m = String(paymentModel || '').match(/^\s*custom\s+(\d+(?:\.\d+)?)\s*%\s*$/i);
    return m ? Number(m[1]) : null;
}

export function formatCustomBurden(percent) {
    return `Custom ${round2(percent)}%`;
}

// Any date (Date, "2026-04-01", "2026-04-01T00:00:00", "04/01/2026") as YYYY-MM-DD
export function toYmd(value) {
    if (!value) return null;
    const s = String(value);
    if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
    const d = new Date(value);
    if (isNaN(d)) return null;
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

// The company service type for a line: exact name first (keeps EVV / non-EVV variants apart),
// then the numeric code
function findServiceType(row, serviceTypes) {
    const names = [row?.serviceCode, row?.description].map(n => String(n || '').trim().toLowerCase()).filter(Boolean);
    const byName = serviceTypes.find(st => names.includes(String(st.Name || '').trim().toLowerCase()));
    if (byName) return byName;
    const code = getServiceCodeNumber(row?.serviceCode);
    return code === null ? null : serviceTypes.find(st => String(st.Code).trim() === String(code)) || null;
}

// An active assignment of this line's provider to this service type, from the participant's
// assigned workers (matched by WorkerId, or by name for lines saved before providers were linked)
function findExistingAssignment(row, serviceType, existingAssignments) {
    const name = String(row?.provider || '').trim().toLowerCase();
    return (existingAssignments || []).find(a => {
        if (a.Active === false || (a.Status !== undefined && a.Status !== null && Number(a.Status) !== 1)) return false;
        if (String(a.ServiceTypeId) !== String(serviceType.ServiceTypeId)) return false;
        if (row?.workerId && a.WorkerId) return String(a.WorkerId) === String(row.workerId);
        return name !== '' && String(a.WorkerName || '').trim().toLowerCase() === name;
    }) || null;
}

/**
 * Works out what Activate still has to write, by comparing the plan's lines with what the server
 * already has (nothing is remembered in the browser, so it gives the same answer anywhere):
 * - per service type: a dollar POS (sum of its lines, plan dates), unless a POS for that service
 *   type already overlaps the plan dates. That one is kept as it is (add-pos can't edit).
 * - per line: an assignment, unless that provider is already assigned to that service type.
 * Only what will actually be written is checked (linked provider, saved amount), so lines that
 * already exist in .NET never block. Returns { problems, posItems: [{ label, amount, request,
 * existing }], assignmentItems: [{ label, serviceName, provider, lineId, request }],
 * skippedAssignments: [{ label, serviceName, provider }] }.
 */
export function buildActivationRequests({ rows, serviceTypes, burdenRates, multipliers = {}, existingPos = [], existingAssignments = [], patientId, startDate, endDate }) {
    const problems = [];
    const start = toYmd(startDate);
    const end = toYmd(endDate);
    const groups = new Map();

    (rows || []).forEach(row => {
        const serviceType = findServiceType(row, serviceTypes || []);
        if (!serviceType) {
            problems.push(`${row.serviceCode || 'A line'}: no matching service type in this company.`);
            return;
        }
        if (!groups.has(serviceType.ServiceTypeId)) groups.set(serviceType.ServiceTypeId, { serviceType, rows: [] });
        groups.get(serviceType.ServiceTypeId).rows.push(row);
    });

    const posItems = [];
    const assignmentItems = [];
    const skippedAssignments = [];
    groups.forEach(({ serviceType, rows: lines }) => {
        const amount = round2(lines.reduce((sum, r) => sum + getRowTotal(r), 0));
        const existing = (existingPos || []).find(p =>
            String(p.ServiceTypeId) === String(serviceType.ServiceTypeId)
            && toYmd(p.StartDate) <= end && toYmd(p.EndDate) >= start) || null;
        if (!existing && !(amount > 0)) problems.push(`${serviceType.Name}: no POS yet, and its lines total $0.`);
        posItems.push({
            label: serviceType.Name,
            amount,
            existing,
            request: {
                PatientId: Number(patientId),
                ServiceTypeId: serviceType.ServiceTypeId,
                StartDate: start,
                EndDate: end,
                Type: 3,
                AuthorizedUnits: amount,
                TimePeriod: 0,
            },
        });

        lines.forEach(row => {
            const provider = row.provider || PLACEHOLDER_PROVIDER_NAME;
            const label = `${serviceType.Name}: ${provider}`;
            if (findExistingAssignment(row, serviceType, existingAssignments)) {
                skippedAssignments.push({ label, serviceName: serviceType.Name, provider });
                return;
            }
            if (!row.workerId) {
                problems.push(`${label}: not linked to a worker or supplier record (pick the provider).`);
                return;
            }
            if (!(getRowTotal(row) > 0)) problems.push(`${label}: no amount saved on the line.`);
            const request = { PatientId: Number(patientId), WorkerId: Number(row.workerId), ServiceTypeId: serviceType.ServiceTypeId };
            if (isVendorRow(row)) {
                // Vendor: no pay or bill rate (sending one makes the API create "Pay Code $…" /
                // "Bill Code $…" records, which a vendor doesn't have; it's paid what its invoices
                // charge). Its amount is carried by the code's dollar POS.
            } else {
                // Worker: Base Pay is the pay rate and Rate/Amount (pay + burden) the bill rate, sent
                // as-is so the assignment matches the plan; the burden is recorded alongside
                if (Number(row.basePay) > 0) request.PayRate = round2(row.basePay);
                if (Number(row.pricingUnit) > 0) request.BillRate = round2(row.pricingUnit);
                const model = String(row.paymentModel || '').trim().toLowerCase();
                const burden = (burdenRates || []).find(b => String(b.Name || '').trim().toLowerCase() === model);
                const customPercent = parseCustomBurdenPercent(row.paymentModel);
                const multiplier = Number(multipliers[row.paymentModel]);
                if (burden) request.BurdenRateId = burden.BurdenRateId;
                else if (customPercent !== null) request.CustomBurdenPercent = round2(customPercent);
                else if (multiplier && multiplier !== 1) request.CustomBurdenPercent = round2((multiplier - 1) * 100);
                else problems.push(`${label}: choose a burden rate (or a custom %) for this worker.`);
            }
            assignmentItems.push({ label, serviceName: serviceType.Name, provider, lineId: row.id, request });
        });
    });

    return { problems, posItems, assignmentItems, skippedAssignments };
}

// Default plan end: start + 1 year - 1 day (9/1/2026 -> 8/31/2027)
export function getDefaultPlanEndDate(startDate) {
    if (!startDate) return null;
    const d = new Date(startDate);
    if (isNaN(d)) return null;
    return new Date(d.getFullYear() + 1, d.getMonth(), d.getDate() - 1);
}

// Keyboard entry order across a row's editable cells (Tab / Shift+Tab)
export function getRowFieldOrder(row) {
    return isVendorRow(row)
        ? ['serviceCode', 'provider', 'pricingUnit', 'frequency']
        : ['serviceCode', 'provider', 'basePay', 'paymentModel', 'frequency'];
}
